"use client";

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  sortRomaReserveLocations,
  type RomaReserveLocation,
} from "./roma-types";

type RomaLocationPickerProps = {
  locations: readonly RomaReserveLocation[];
  eyebrow?: string;
  heading?: string;
  intro?: string;
  className?: string;
  id?: string;
};

export function RomaLocationPicker({
  locations,
  eyebrow = "Which location?",
  heading = "Pick your Roma, then reserve your table.",
  intro,
  className,
  id = "picker",
}: RomaLocationPickerProps) {
  const sortedLocations = sortRomaReserveLocations(locations);
  const defaultSlug =
    sortedLocations.find((location) => location.slug === "southern-highlands")?.slug ??
    sortedLocations[0]?.slug ??
    "southern-highlands";
  const [activeSlug, setActiveSlug] = useState(defaultSlug);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash !== "picker" && hash !== "roma-reservation-widget") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [id]);

  if (sortedLocations.length === 0) {
    return null;
  }

  const activeLocation =
    sortedLocations.find((location) => location.slug === activeSlug) ??
    sortedLocations[0];

  return (
    <section
      id="roma-reservation-widget"
      className={cn("scroll-mt-28 lg:scroll-mt-32", className)}
      aria-label="Roma location picker"
    >
      <div
        id={id}
        className="rounded-xl border border-[var(--roma-parchment-line,#e3e3e3)] bg-[var(--roma-ivory,#fffaf0)] p-5 shadow-[var(--shadow-soft,0_18px_44px_-22px_rgb(0_0_0_/_0.12))] sm:p-6"
      >
        <p className="roma-eyebrow text-[var(--roma-terracotta,#f45c24)]">
          {eyebrow}
        </p>
        <h2 className="mt-2 font-serif text-2xl font-normal tracking-tight text-[var(--roma-espresso,#2b1c17)] sm:text-3xl">
          {heading}
        </h2>
        {intro ? (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--roma-espresso,#2b1c17)]/72">
            {intro}
          </p>
        ) : null}

        <div
          className="mt-5 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Choose a location"
        >
          {sortedLocations.map((location) => {
            const isActive = location.slug === activeSlug;

            return (
              <button
                key={location.slug}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={cn(
                  "min-h-11 rounded-xl px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta,#f45c24)]",
                  isActive
                    ? "bg-[var(--roma-espresso,#2b1c17)] text-[var(--roma-ivory,#fffaf0)]"
                    : "bg-[var(--roma-parchment,#f6f4ef)] text-[var(--roma-espresso,#2b1c17)] hover:bg-[var(--roma-parchment-line,#e3e3e3)]",
                )}
                onClick={() => setActiveSlug(location.slug)}
              >
                {location.eyebrow}
              </button>
            );
          })}
        </div>

        {activeLocation ? (
          <div role="tabpanel" className="mt-5 space-y-4">
            <p className="text-sm leading-relaxed text-[var(--roma-espresso,#2b1c17)]/78">
              {activeLocation.addressLine1}
              <br />
              {activeLocation.addressLine2}
              <br />
              <a
                href={`tel:${activeLocation.phoneHref}`}
                className="font-semibold text-[var(--roma-terracotta,#f45c24)] underline-offset-2 hover:underline"
              >
                {activeLocation.phoneDisplay}
              </a>
            </p>
            <Button
              asChild
              className="min-h-11 rounded-xl bg-[var(--roma-terracotta,#f45c24)] text-[var(--roma-espresso,#0d0d0d)] hover:bg-[var(--roma-saffron-deep,#d94e1a)]"
            >
              <a
                href={activeLocation.reserveUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-track="reservation_clicked"
                data-track-location="roma_location_picker"
                data-track-label={activeLocation.slug}
              >
                Reserve Now
              </a>
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
